import { Box, TextField } from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';
import { useSyncedInputValue } from './useSyncedInputValue';

interface BetweenEditorProps {
    value: string;
    inputType?: string;
    onChange: (value: string) => void;
    // When set, min and max are swapped if the user enters them in the wrong order (e.g.
    // min=25, max=15 becomes min=15, max=25) instead of being left as an invalid range.
    swapIfInverted?: boolean;
}

function BetweenEditor({ value, inputType, onChange, swapIfInverted }: BetweenEditorProps) {
    const { t } = useLanguage();

    const values = value ? value.split('|') : ['', ''];
    const minValue = values[0] || '';
    const maxValue = values[1] || '';

    const minRef = useSyncedInputValue(minValue);
    const maxRef = useSyncedInputValue(maxValue);

    const handleBlur = () => {
        let min = minRef.current?.value || '';
        let max = maxRef.current?.value || '';
        if (swapIfInverted && min !== '' && max !== '' && !Number.isNaN(Number(min)) && !Number.isNaN(Number(max)) && Number(min) > Number(max)) {
            [min, max] = [max, min];
        }
        onChange(`${min}|${max}`);
    };

    const type = inputType === 'date' ? 'date' : inputType === 'number' ? 'number' : 'text';

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
            <TextField
                inputRef={minRef}
                type={type}
                size="small"
                defaultValue={minValue}
                onBlur={handleBlur}
                placeholder={t('pages.clients.queryBuilder.simple.between.min')}
                sx={{ flex: '1 1 0', minWidth: 80 }}
                InputLabelProps={type === 'date' ? { shrink: true } : undefined}
            />
            <Box sx={{ px: 1, color: 'text.secondary' }}>{t('pages.clients.queryBuilder.simple.between.and')}</Box>
            <TextField
                inputRef={maxRef}
                type={type}
                size="small"
                defaultValue={maxValue}
                onBlur={handleBlur}
                placeholder={t('pages.clients.queryBuilder.simple.between.max')}
                sx={{ flex: '1 1 0', minWidth: 80 }}
                InputLabelProps={type === 'date' ? { shrink: true } : undefined}
            />
        </Box>
    );
}

export default BetweenEditor;
